/**
 * Location Sharing. Doc: docs/overall-plan/02-MANGO-PERSONAL/location-sharing
 * Wired to GET/POST /api/v1/location-sharing.
 */
import React, { useEffect, useState } from 'react';
import { locationSharingApi, type SharedLocation } from '../services/api';

const LocationSharing: React.FC = () => {
  const [list, setList] = useState<SharedLocation[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showShare, setShowShare] = useState(false);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [stopping, setStopping] = useState<string | null>(null);
  const [geolocating, setGeolocating] = useState(false);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }
    setGeolocating(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(6));
        setLng(pos.coords.longitude.toFixed(6));
        setGeolocating(false);
      },
      () => {
        setError('Could not get your location. Check browser permissions or enter manually.');
        setGeolocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const loadData = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await locationSharingApi.getShared();
      if (res?.success) {
        setList(Array.isArray(res?.data) ? res.data : []);
        setMessage(res?.message ?? null);
      } else setError(res?.error || 'Failed to load');
    } catch {
      setError('Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleShare = async () => {
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);
    if (isNaN(latitude) || isNaN(longitude)) {
      setError('Enter valid latitude and longitude');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await locationSharingApi.share({
        latitude,
        longitude,
        label: label.trim() || undefined,
      });
      if (res?.success) {
        setLat('');
        setLng('');
        setLabel('');
        setShowShare(false);
        loadData(true);
      } else setError(res?.error || 'Failed to share');
    } catch {
      setError('Failed to share');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStop = async (shareId?: string) => {
    setStopping(shareId ?? 'all');
    setError(null);
    try {
      const res = await locationSharingApi.stop(shareId);
      if (res?.success) loadData(true);
      else setError(res?.error || 'Failed to stop');
    } catch {
      setError('Failed to stop');
    } finally {
      setStopping(null);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>📍 Location Sharing</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
              Share your location with connections or for events
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setShowShare((s) => !s)}
              style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '8px', color: '#11998e', cursor: 'pointer', fontSize: '14px' }}
            >
              Share Location
            </button>
            <button
              type="button"
              onClick={() => loadData(true)}
              disabled={refreshing}
              style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', cursor: refreshing ? 'not-allowed' : 'pointer', fontSize: '14px' }}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {showShare && (
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
          <input
            type="text"
            placeholder="Latitude (e.g. -33.8688)"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
          />
          <input
            type="text"
            placeholder="Longitude (e.g. 151.2093)"
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
          />
          <input
            type="text"
            placeholder="Label (e.g. Home, Office)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
          />
          <button
            type="button"
            onClick={handleUseMyLocation}
            disabled={geolocating}
            style={{ marginBottom: '12px', padding: '8px 16px', background: '#11998e', border: 'none', borderRadius: '8px', color: 'white', cursor: geolocating ? 'not-allowed' : 'pointer', fontSize: '14px' }}
          >
            {geolocating ? 'Getting location…' : 'Use my location'}
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={handleShare} disabled={submitting} style={{ padding: '8px 16px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
              {submitting ? 'Sharing…' : 'Share'}
            </button>
            <button type="button" onClick={() => { setShowShare(false); setLat(''); setLng(''); setLabel(''); }} style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}
      {message && !error && list.length === 0 && <p style={{ marginBottom: '16px', fontSize: '14px', color: '#64748b' }}>{message}</p>}
      {list.length === 0 && !error && !showShare && (
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
          No active sharing. Add a location above to start sharing.
        </div>
      )}
      {list.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {list.map((item) => (
            <li
              key={item.id}
              style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}
            >
              <div>
                <strong>{item.label || 'Shared location'}</strong>
                <span style={{ marginLeft: '8px', fontSize: '12px', color: '#64748b' }}>
                  {item.latitude.toFixed(4)}, {item.longitude.toFixed(4)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleStop(item.id)}
                disabled={stopping === item.id}
                style={{ padding: '6px 12px', background: '#dc2626', border: 'none', borderRadius: '6px', color: 'white', cursor: stopping === item.id ? 'not-allowed' : 'pointer', fontSize: '12px' }}
              >
                {stopping === item.id ? '…' : 'Stop'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LocationSharing;
